import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Row,
  Col,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import DataTable from 'react-data-table-component';
import notify, { MESSAGES } from '../../../utils/notify';
import classnames from 'classnames';
import useTenderOpeningTabVisibility from '../hooks/useTenderOpeningTabVisibility';
import useTenderOpeningList from '../hooks/useTenderOpeningList';
import useTenderOpeningSummary from '../hooks/useTenderOpeningSummary';
import { getTenderOpeningBadgeColor } from '../utils/tenderOpeningHelpers';
import ActionButton from '../../../Components/Common/ActionButton';
import { EmptyState } from '../../../Components/Common/ListStates';
import { usePermission } from '../../../contexts/PermissionContext';
import {
  ActiveFilterChips,
  ClearFiltersButton,
  ListFilterSelect,
  ListSearch,
} from '../../../Components/Common/ListFilters';
import {
  ContractBadge,
  DeadlineChip,
  ProductAvatar,
} from '../../Procurement/component/ListBadges';
import {
  exportTenderOpeningList,
  getTenderQuickFilter,
  isTenderClosed,
} from '../utils/tenderOpeningListExport';
import StatusBadge from '../../../Components/Common/StatusBadge';

// Badge color from the helper -> tone used by the pill, stat card and mobile card.
const STATUS_TONE = {
  primary: 'info',
  warning: 'warning',
  success: 'success',
};

const STAT_CARDS = [
  {
    key: 'total',
    filter: '',
    label: 'Total Tender',
    icon: 'ri-stack-line',
    tone: 'total',
  },
  {
    key: 'open',
    filter: '0',
    label: 'Open',
    icon: 'ri-inbox-archive-line',
    tone: 'open',
  },
  {
    key: 'inProgress',
    filter: '1',
    label: 'In Progress',
    icon: 'ri-hourglass-2-line',
    tone: 'progress',
  },
  {
    key: 'done',
    filter: '2',
    label: 'Done',
    icon: 'ri-checkbox-circle-line',
    tone: 'done',
  },
];

const STEP_DEFS = [
  { key: 'reqHps', label: 'Request HPS', icon: 'ri-price-tag-3-line' },
  {
    key: 'inviteDmut',
    label: 'Invite Registered Supplier',
    icon: 'ri-team-line',
  },
  {
    key: 'reqFreight',
    label: 'Request Freight Availability',
    icon: 'ri-ship-2-line',
    isApplicable: item => Boolean(item.isReqFreightAvailabilityEnable),
  },
];

const getSteps = item =>
  STEP_DEFS.map(step => ({
    ...step,
    isApplicable: step.isApplicable ? step.isApplicable(item) : true,
    isDone: item[step.key] === true,
  }));

const getStatusTone = status =>
  STATUS_TONE[getTenderOpeningBadgeColor(status)] || 'info';

const StatusPill = ({ status }) => {
  if (!status) return <span className='text-muted'>-</span>;

  return (
    <StatusBadge tone={getStatusTone(status)} label={status} title={status} />
  );
};

const WorkflowSteps = ({ item }) => {
  const steps = getSteps(item);
  const applicable = steps.filter(step => step.isApplicable);
  const doneCount = applicable.filter(step => step.isDone).length;
  const panitiaSent = item.invitePanitia === true;

  return (
    <div className='to-steps'>
      <div className='to-steps-track'>
        {steps.map((step, index) => {
          let state = 'Open';
          if (!step.isApplicable) state = 'Not required';
          else if (step.isDone) state = 'Done';

          return (
            <React.Fragment key={step.key}>
              {index > 0 && (
                <span
                  className={classnames('to-step-link', {
                    'is-done': steps[index - 1].isDone,
                  })}
                />
              )}
              <span
                className={classnames('to-step', {
                  'is-done': step.isApplicable && step.isDone,
                  'is-na': !step.isApplicable,
                })}
                title={`${step.label}: ${state}`}
                aria-label={`${step.label}: ${state}`}
              >
                <i
                  className={
                    step.isApplicable && step.isDone
                      ? 'ri-check-line'
                      : step.icon
                  }
                  aria-hidden='true'
                />
              </span>
            </React.Fragment>
          );
        })}
        <span className='to-step-split' aria-hidden='true' />
        <span
          className={classnames('to-step', { 'is-done': panitiaSent })}
          title={`Invite Panitia Tender: ${panitiaSent ? 'Sent' : 'Not sent'}`}
          aria-label={`Invite Panitia Tender: ${
            panitiaSent ? 'Sent' : 'Not sent'
          }`}
        >
          <i
            className={panitiaSent ? 'ri-check-line' : 'ri-government-line'}
            aria-hidden='true'
          />
        </span>
      </div>
      <div className='to-steps-meta'>
        <span>
          <strong>{doneCount}</strong>/{applicable.length} steps
        </span>
        <span>
          {applicable.length
            ? Math.round((doneCount / applicable.length) * 100)
            : 0}
          %
        </span>
      </div>
    </div>
  );
};

const StatCard = ({ card, value, isActive, onSelect }) => {
  const total = value?.total || 0;
  const count = value?.[card.key];
  const share =
    card.key !== 'total' && total > 0 && typeof count === 'number'
      ? Math.round((count / total) * 100)
      : null;

  return (
    <button
      type='button'
      className={classnames('to-stat', `to-stat--${card.tone}`, {
        'is-active': isActive,
      })}
      aria-pressed={isActive}
      onClick={onSelect}
    >
      <span className='to-stat-icon'>
        <i className={card.icon} aria-hidden='true' />
      </span>
      <span>
        <span className='to-stat-label'>{card.label}</span>
        <span className='to-stat-value'>
          {typeof count === 'number' ? count : '–'}
        </span>
      </span>
      {share !== null && <span className='to-stat-share'>{share}%</span>}
    </button>
  );
};

const STATUS_OPTIONS = [
  { value: '0', label: 'Open' },
  { value: '1', label: 'In Progress' },
  { value: '2', label: 'Done' },
];

const SkeletonRows = ({ count = 5 }) => (
  <div className='w-100 px-3 py-2'>
    {Array.from({ length: count }).map((_, index) => (
      <div key={index} className='d-flex align-items-center gap-3 py-3'>
        <div className='to-skeleton' style={{ width: '9%' }} />
        <div className='to-skeleton' style={{ width: '28%' }} />
        <div className='to-skeleton' style={{ width: '12%' }} />
        <div className='to-skeleton' style={{ width: '20%' }} />
        <div className='to-skeleton flex-grow-1' />
      </div>
    ))}
  </div>
);

const TenderOpeningTable = ({
  procurementTypeHeader,
  refreshTrigger = 0,
  quickFilter = '',
  onQuickFilterChange,
  exportName = 'List',
}) => {
  const navigate = useNavigate();
  const { can } = usePermission();
  const [isExporting, setIsExporting] = useState(false);
  const activeQuickFilter = getTenderQuickFilter(quickFilter);
  const { canViewProcurementTypeHeader } = useTenderOpeningTabVisibility();
  const canViewTable = canViewProcurementTypeHeader(procurementTypeHeader);

  const {
    data,
    totalRows,
    currentPage,
    rowsPerPage,
    searchText,
    tenderOpeningStatusFilter,
    isLoading,
    startEntry,
    endEntry,
    handleSearch,
    handleStatusFilter,
    handlePageChange,
    handlePerRowsChange,
    handleSort,
    clearFilters: clearListFilters,
    fetchAllRows,
  } = useTenderOpeningList({
    procurementTypeHeader,
    refreshTrigger,
    enabled: canViewTable,
    deadlineStatus: activeQuickFilter?.params.DeadlineStatus || '',
  });

  const { summary } = useTenderOpeningSummary({
    procurementTypeHeader,
    refreshTrigger,
    enabled: canViewTable,
  });

  if (!canViewTable) {
    return (
      <div className='to-panel'>
        <div className='to-empty'>
          <span className='to-empty-icon'>
            <i className='ri-lock-line' aria-hidden='true' />
          </span>
          <p className='text-muted mb-0'>
            You do not have access to view this procurement category.
          </p>
        </div>
      </div>
    );
  }

  const clearFilters = () => {
    clearListFilters();
    onQuickFilterChange?.('');
  };

  const handleExport = async format => {
    setIsExporting(true);
    try {
      const rows = await fetchAllRows();
      if (!rows.length) {
        notify.info(MESSAGES.noDataToExport);
        return;
      }
      exportTenderOpeningList(rows, { format, sheetName: exportName });
    } catch (error) {
      notify.apiError(error, 'export tender opening data');
    } finally {
      setIsExporting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));
  const filterChips = [
    searchText && {
      key: 'search',
      label: 'Search',
      value: searchText,
      onRemove: () => handleSearch({ target: { value: '' } }),
    },
    tenderOpeningStatusFilter !== '' && {
      key: 'status',
      label: 'Status',
      value:
        STATUS_OPTIONS.find(o => o.value === tenderOpeningStatusFilter)
          ?.label ?? tenderOpeningStatusFilter,
      onRemove: () => handleStatusFilter(''),
    },
    activeQuickFilter && {
      key: 'quick',
      label: 'Highlight',
      value: `Tender ${activeQuickFilter.label}`,
      onRemove: () => onQuickFilterChange?.(''),
    },
  ].filter(Boolean);
  const hasFilters = filterChips.length > 0;
  const goToDetail = row => navigate(`/apps-tenderopening/dtl/${row.id}`);

  const emptyState = (
    <EmptyState
      title='No tender found'
      emptyHint='Procurements appear here once they are submitted from Planning & Procurement.'
      filteredHint='Nothing matches the current search or status filter.'
      icon='ri-inbox-archive-line'
      hasFilters={hasFilters}
      onClear={clearFilters}
      secondaryAction={
        can('canRead', '/apps-procurement/view')
          ? {
              label: 'Go to Procurement',
              icon: 'ri-arrow-right-line',
              onClick: () => navigate('/apps-procurement/view'),
            }
          : undefined
      }
    />
  );

  const columns = [
    {
      id: 'idProc',
      name: 'ID Proc',
      selector: row => row.idProc,
      cell: row => <span className='to-id'>{row.idProc || '-'}</span>,
      width: '9%',
      sortable: true,
    },
    {
      id: 'title',
      name: 'Tender',
      selector: row => row.title,
      cell: row => (
        <div className='d-flex align-items-center gap-2 py-2 w-100'>
          <ProductAvatar name={row.productType?.main} />
          <div className='pv-text-col flex-grow-1'>
            <div className='to-title' title={row.title}>
              {row.title || '-'}
            </div>
            <div className='to-product'>
              <span className='to-product-name'>{row.productType?.main}</span>
              {row.productType?.sub && row.productType.sub !== '-' && (
                <span className='text-truncate ms-2'>
                  · {row.productType.sub}
                </span>
              )}
            </div>
          </div>
        </div>
      ),
      width: '20%',
      sortable: true,
    },
    {
      id: 'period',
      name: 'Period',
      selector: row => row.period,
      cell: row =>
        row.contractType ? (
          <ContractBadge contractType={row.contractType} />
        ) : (
          <span className='text-muted'>-</span>
        ),
      width: '7%',
      sortable: true,
    },
    {
      id: 'procType',
      name: 'Proc Type',
      selector: row => row.procType,
      cell: row => <span className='to-tag'>{row.procType || '-'}</span>,
      width: '9%',
    },
    {
      id: 'deadline',
      name: 'Deadline',
      cell: row => (
        <DeadlineChip
          deadline={row.proposalSubmissionDeadline}
          isClosed={isTenderClosed(row)}
        />
      ),
      width: '10%',
      sortable: true,
    },
    {
      id: 'submittedDate',
      name: 'Submitted',
      selector: row => row.submittedDate,
      cell: row => (
        <span className='to-date'>
          <i className='ri-calendar-line me-2' aria-hidden='true' />
          {row.submittedDate}
        </span>
      ),
      width: '8%',
      sortable: true,
    },
    {
      id: 'updateDate',
      name: 'Last Update',
      selector: row => row.updateDateRaw,
      cell: row => (
        <span className='to-date'>
          <i className='ri-time-line me-2' aria-hidden='true' />
          {row.updateDate}
        </span>
      ),
      width: '8%',
      sortable: true,
    },
    {
      id: 'progress',
      name: 'Workflow Progress',
      cell: row => <WorkflowSteps item={row} />,
      width: '15%',
      sortable: false,
    },
    {
      id: 'status',
      name: 'Status',
      selector: row => row.status,
      cell: row => <StatusPill status={row.status} />,
      width: '8%',
      sortable: true,
    },
    {
      name: <span className='text-center w-100'>Action</span>,
      cell: row => (
        <div className='text-center w-100'>
          <ActionButton
            variant='icon'
            size='sm'
            icon='ri-arrow-right-up-line'
            label='Open detail'
            onClick={e => {
              e.stopPropagation();
              goToDetail(row);
            }}
          />
        </div>
      ),
      width: '6%',
      center: true,
      sortable: false,
    },
  ];

  const MobileCard = ({ item }) => (
    <div
      className={`to-mcard to-mcard--${getStatusTone(item.status)} d-md-none`}
    >
      <div className='d-flex justify-content-between align-items-center gap-2 mb-2'>
        <span className='d-inline-flex align-items-center gap-2'>
          <span className='to-id'>{item.idProc}</span>
          <ContractBadge contractType={item.contractType} />
        </span>
        <StatusPill status={item.status} />
      </div>

      <div className='d-flex align-items-center gap-2'>
        <ProductAvatar name={item.productType?.main} />
        <div className='pv-text-col flex-grow-1'>
          <div className='to-title'>{item.title}</div>
          <div className='to-product'>
            <span className='to-product-name'>{item.productType?.main}</span>
            {item.productType?.sub && item.productType.sub !== '-' && (
              <span className='text-truncate ms-2'>
                · {item.productType.sub}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className='to-mcard-meta'>
        <div>
          <span>Submitted</span>
          <span>{item.submittedDate}</span>
        </div>
        <div>
          <span>Last Update</span>
          <span>{item.updateDate}</span>
        </div>
        <div>
          <span>Proc Type</span>
          <span>{item.procType}</span>
        </div>
        <div>
          <span>Period</span>
          <span>{item.period}</span>
        </div>
      </div>

      <div className='mb-3'>
        <DeadlineChip
          deadline={item.proposalSubmissionDeadline}
          isClosed={isTenderClosed(item)}
        />
      </div>

      <div className='mb-3'>
        <WorkflowSteps item={item} />
      </div>

      <ActionButton
        variant='secondary'
        size='sm'
        icon='ri-eye-line'
        className='w-100'
        onClick={() => goToDetail(item)}
      >
        View Detail
      </ActionButton>
    </div>
  );

  return (
    <>
      <Row className='g-3 mb-3'>
        {STAT_CARDS.map(card => (
          <Col key={card.key} xs={6} xl={3}>
            <StatCard
              card={card}
              value={summary}
              isActive={tenderOpeningStatusFilter === card.filter}
              onSelect={() =>
                handleStatusFilter(
                  tenderOpeningStatusFilter === card.filter && card.filter
                    ? ''
                    : card.filter
                )
              }
            />
          </Col>
        ))}
      </Row>

      <div className='to-panel'>
        <div className='to-toolbar'>
          <ListSearch
            value={searchText}
            onChange={handleSearch}
            placeholder='Search by title or procurement no...'
            ariaLabel='Search tender'
          />
          <ListFilterSelect
            label='Status'
            value={tenderOpeningStatusFilter}
            onChange={handleStatusFilter}
            allLabel='All Status'
            options={STATUS_OPTIONS}
          />
          <ClearFiltersButton
            count={filterChips.length}
            onClick={clearFilters}
          />
          <span className='to-toolbar-meta'>
            {isLoading
              ? 'Loading...'
              : `Showing ${startEntry}–${endEntry} of ${totalRows} tenders`}
          </span>
          <UncontrolledDropdown>
            <DropdownToggle
              tag='button'
              type='button'
              className='btn btn-light btn-sm d-inline-flex align-items-center'
              disabled={isExporting || isLoading || !totalRows}
            >
              {isExporting ? (
                <span
                  className='spinner-border spinner-border-sm me-2'
                  role='status'
                  aria-hidden='true'
                />
              ) : (
                <i className='ri-download-2-line me-2' aria-hidden='true' />
              )}
              Export
            </DropdownToggle>
            <DropdownMenu end>
              <DropdownItem onClick={() => handleExport('xlsx')}>
                <i className='ri-file-excel-2-line align-middle me-2 text-success'></i>
                Export to Excel
              </DropdownItem>
              <DropdownItem onClick={() => handleExport('csv')}>
                <i className='ri-file-text-line align-middle me-2 text-muted'></i>
                Export to CSV
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        </div>

        <ActiveFilterChips chips={filterChips} />

        <div className='to-legend d-none d-md-flex'>
          <span className='fw-medium'>Workflow steps:</span>
          {STEP_DEFS.map(step => (
            <span key={step.key} className='to-legend-item'>
              <i className={`${step.icon} me-2`} aria-hidden='true' />
              {step.label}
            </span>
          ))}
        </div>

        <div className='d-md-none p-3'>
          {isLoading && <SkeletonRows count={3} />}
          {!isLoading &&
            data.items?.map((item, index) => (
              <MobileCard key={item.id || index} item={item} />
            ))}
          {!isLoading && !data.items?.length && emptyState}
        </div>

        <div className='d-none d-md-block to-table-wrap'>
          <DataTable
            columns={columns}
            data={data.items}
            pagination={false}
            onSort={handleSort}
            sortServer
            defaultSortFieldId='idProc'
            defaultSortAsc={false}
            onRowClicked={goToDetail}
            highlightOnHover
            pointerOnHover
            persistTableHead
            progressPending={isLoading}
            progressComponent={<SkeletonRows />}
            noDataComponent={emptyState}
            customStyles={{
              headCells: {
                style: { paddingLeft: '12px', paddingRight: '12px' },
              },
              cells: {
                style: {
                  paddingLeft: '12px',
                  paddingRight: '12px',
                  wordBreak: 'break-word',
                },
              },
              table: { style: { width: '100%' } },
              tableWrapper: { style: { display: 'table' } },
            }}
          />
        </div>

        <div className='to-footer'>
          <div className='d-flex align-items-center gap-2'>
            <span>Rows per page</span>
            <select
              className='form-select form-select-sm'
              style={{ width: 'auto' }}
              value={rowsPerPage}
              onChange={e => handlePerRowsChange(parseInt(e.target.value, 10))}
              aria-label='Rows per page'
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span className='to-footer-range'>
              {startEntry}–{endEntry} of <strong>{totalRows}</strong> items
            </span>
          </div>

          <div className='to-pager'>
            <button
              type='button'
              className='to-pager-btn'
              disabled={currentPage <= 1 || isLoading}
              onClick={() => handlePageChange(1)}
              aria-label='First page'
              title='First page'
            >
              <i className='ri-skip-back-line' aria-hidden='true' />
            </button>
            <button
              type='button'
              className='to-pager-btn'
              disabled={currentPage <= 1 || isLoading}
              onClick={() => handlePageChange(currentPage - 1)}
              aria-label='Previous page'
              title='Previous page'
            >
              <i className='ri-arrow-left-s-line' aria-hidden='true' />
            </button>
            <span className='to-pager-current'>
              Page {currentPage} of {totalPages}
            </span>
            <button
              type='button'
              className='to-pager-btn'
              disabled={currentPage >= totalPages || isLoading}
              onClick={() => handlePageChange(currentPage + 1)}
              aria-label='Next page'
              title='Next page'
            >
              <i className='ri-arrow-right-s-line' aria-hidden='true' />
            </button>
            <button
              type='button'
              className='to-pager-btn'
              disabled={currentPage >= totalPages || isLoading}
              onClick={() => handlePageChange(totalPages)}
              aria-label='Last page'
              title='Last page'
            >
              <i className='ri-skip-forward-line' aria-hidden='true' />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default React.memo(TenderOpeningTable);
